import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Replacement for ngx-avatar (abandoned, View Engine only).
 * Supports the same inputs used across the app: name, src, size, round, textSizeRatio.
 * Shows an image if src loads; falls back to initials on error or when src is absent.
 * Background color is dynamically generated from the name hash (matches ngx-avatar behavior).
 */
@Component({
  standalone: true,
    selector: 'ngx-avatar',
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div [ngStyle]="containerStyle" [class.cs-avatar--round]="round" class="cs-avatar">
      <img *ngIf="src && !imgError"
           [src]="src"
           [ngStyle]="imgStyle"
           (error)="imgError = true"
           alt="">
      <span *ngIf="!src || imgError" [ngStyle]="textStyle">{{ initials }}</span>
    </div>
  `,
    styles: [`
    .cs-avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 600;
      overflow: hidden;
      vertical-align: middle;
      flex-shrink: 0;
    }
    .cs-avatar--round { border-radius: 50%; }
    img { width: 100%; height: 100%; object-fit: cover; }
  `]
})
export class CsAvatarComponent implements OnChanges {
  @Input() name = '';
  @Input() src = '';
  @Input() size: number | string = 40;
  @Input() round = false;
  @Input() textSizeRatio: number | string = 3;

  // Fixed primary color matching the app theme (indigo-800 = #283593)
  // Matches the visual shown in QA captures — consistent across all avatars
  private static readonly COLORS = ['#283593'];

  initials = '';
  imgError = false;
  containerStyle: Record<string, string> = {};
  imgStyle: Record<string, string> = {};
  textStyle: Record<string, string> = {};

  ngOnChanges(): void {
    this.imgError = false;
    this.initials = this.buildInitials(this.name);
    const px = `${this.size}px`;
    const fontSize = `${Math.floor(+this.size / +this.textSizeRatio)}px`;
    const bg = this.colorFromName(this.name);
    this.containerStyle = { width: px, height: px, 'font-size': fontSize, 'background-color': bg };
    this.imgStyle = { width: px, height: px };
    this.textStyle = { 'line-height': px };
  }

  private colorFromName(name: string): string {
    if (!name) { return CsAvatarComponent.COLORS[0]; }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    const index = Math.abs(hash) % CsAvatarComponent.COLORS.length;
    return CsAvatarComponent.COLORS[index];
  }

  private buildInitials(name: string): string {
    if (!name) { return '?'; }
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) { return '?'; }
    if (parts.length === 1) { return parts[0][0].toUpperCase(); }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
}
